/* eslint-disable no-console */
import { nanoid } from 'nanoid';
import {
  getBrowserName,
  getBrowserPlatform,
  getBrowserVersionNumber,
} from '@ardoq/common-helpers';
import { ArbitraryMetadata, DynamicMetadataFn, Team } from './types';
import { pick } from 'lodash';

export const isDevelopmentMode = () => import.meta.env.DEV;

type NetworkInformation = {
  downlink: number;
  effectiveType: string;
  rtt: number;
  saveData: boolean;
};

type DeviceInformation = {
  hardwareConcurrency?: number;
  deviceMemory?: number;
  memory?: Record<string, number>;
};

type IndexedMetadata = {
  duration: number;
  threshold: number;
  isAboveThreshold: boolean;
  isAboveTolerable: boolean;
  viewId?: string;
  buildVersion: string;
  networkInformation?: NetworkInformation;
  deviceInformation: DeviceInformation;
  browserWentOffline: boolean;
  documentVisibilityRatio: number;
  isInIframe: boolean;
  team: Team;
};

type LogPerformanceMeasurement = {
  name: string;
  indexedMetadata: IndexedMetadata;
  arbitraryMetadata?: ArbitraryMetadata;
};

const sessionCorrelationId = nanoid();

const logPerformanceMeasurement = ({
  name,
  indexedMetadata,
  arbitraryMetadata,
}: LogPerformanceMeasurement) => {
  // Destruct some keys from logdata to ensure that they're indexed in Kibana
  return fetch('/api/log', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      level: 'info',
      message: 'Performance Measurement',
      measurement: name,
      sessionCorrelationId,
      ...indexedMetadata,
      metadata: JSON.stringify(arbitraryMetadata),
      browser: `${getBrowserName()} ${getBrowserVersionNumber()}`,
      platform: getBrowserPlatform(),
      userAgent: navigator.userAgent,
      uri: window.location.href,
      referrer: document.referrer,
    }),
  });
};

export const startPerformanceMeasurement = (name: string) => {
  try {
    const mark1 = `${name} 1`;
    window.performance.mark(mark1);
  } catch (e) {
    if (isDevelopmentMode()) {
      console.warn('[PERFORMANCE]: Failed to measure performance', e);
    }
  }
};

export type MeasurementProps = {
  name: string;
  threshold: number;
  viewId?: string;
  metadata?: ArbitraryMetadata;
  buildVersion: string;
  dynamicMetadataFn: DynamicMetadataFn;
  browserWentOffline: boolean;
  documentVisibilityRatio: number;
  isInIframe: boolean;
  team: Team;
};

const getNetworkInformation = (): NetworkInformation | undefined => {
  const connection = (navigator as any).connection;

  return (
    connection &&
    pick(connection, ['downlink', 'effectiveType', 'rtt', 'saveData'])
  );
};

/**
 * This function uses deprecated, non-standard, and experimental browser APIs.
 * There is no universal way to get this information, so we try our best to get
 * as much as we can. Luckily, Chrome and Edge support these APIs, which covers
 * about 90% of our users as of this writing.
 */
const getDeviceInformation = (): DeviceInformation => {
  const memoryInfo = (performance as any).memory;

  return {
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    memory: memoryInfo && {
      ...pick(memoryInfo, [
        'jsHeapSizeLimit',
        'totalJSHeapSize',
        'usedJSHeapSize',
      ]),
      used: memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit,
    },
  };
};

let measures = 0;
/**
 * Specify how a performance measurement should be logged.
 *
 * threshold should typically be 1s for rendering and 500ms for view models
 *
 * viewId should specify which view this measurement relates to. If it's a generic
 * model calculation viewId can be null. This makes it easier to group related metrics in kibana
 *
 * metadata is arbitrary (serializable) data that should contain more info about
 * the measurement, for example how many nodes were actually rendered in the view
 *
 */
export const endPerformanceMeasurement = ({
  name,
  threshold,
  buildVersion,
  dynamicMetadataFn,
  viewId,
  metadata,
  browserWentOffline,
  documentVisibilityRatio,
  isInIframe,
  team,
}: MeasurementProps) => {
  if (measures > 1000) {
    window.performance.clearMeasures();
    measures = 0;
  }
  const mark1 = `${name} 1`;
  const mark2 = `${name} 2`;
  try {
    // Only measure if mark 1 is set
    if (window.performance.getEntriesByName(mark1).length) {
      window.performance.mark(mark2);
      const { duration } = window.performance.measure(
        name,
        mark1,
        mark2
      ) as unknown as PerformanceMeasure;

      if (isDevelopmentMode() && duration > threshold) {
        console.warn(
          `[PERFORMANCE]: ${name} took longer than ${threshold}ms, done in ${duration}ms`,
          {
            viewId,
            ...metadata,
          }
        );
      }
      logPerformanceMeasurement({
        name,
        indexedMetadata: {
          ...dynamicMetadataFn(),
          buildVersion,
          duration,
          viewId,
          isAboveThreshold: duration > threshold,
          isAboveTolerable: duration > 4 * threshold,
          threshold,
          networkInformation: getNetworkInformation(),
          deviceInformation: getDeviceInformation(),
          browserWentOffline,
          documentVisibilityRatio,
          isInIframe,
          team,
        },
        arbitraryMetadata: metadata,
      });
      window.performance.clearMarks(mark1);
      window.performance.clearMarks(mark2);
      measures++;
    }
  } catch (e) {
    if (isDevelopmentMode()) {
      console.warn('[PERFORMANCE]: Failed to measure performance', e);
    }
  }
};
